/**
 * Glossário compartilhado entre todos os assessments (IGA, BS4IT, futuros pilares).
 *
 * Uso:
 *   1. Envolver o app/página com <GlossaryProvider>
 *   2. Usar renderWithGlossary(label) para injetar botões (?) nos termos
 *   3. Clique no (?) abre modal centralizado com título + descrição
 */
import { createContext, useContext, useState, ReactNode } from 'react'
import { X } from 'lucide-react'

export type GlossaryEntry = { title: string; body: string }

export const GLOSSARY: Record<string, GlossaryEntry> = {
  // ─── Plataformas & Legado ──────────────────────────────────────────
  'AS/400': {
    title: 'AS/400 / IBM i / iSeries',
    body: 'Plataforma midrange da IBM (atualmente IBM i) usada em sistemas legados desde os anos 80. Roda aplicações em RPG/CL/COBOL com modelo de segurança proprietário (user profiles, authorities, libraries) — não fala protocolos modernos como SAML/OIDC. Conectar IGA exige conector específico.',
  },
  'CMDB': {
    title: 'CMDB — Configuration Management Database',
    body: 'Banco de dados que mantém inventário de ativos (servidores, apps, dispositivos) e suas relações de dependência. Base para gestão de mudanças, resposta a incidentes e análise de impacto. CMDB atualizada é pré-requisito para SegInfo madura: você precisa saber o que tem antes de proteger.',
  },
  'Hypervisor': {
    title: 'Hypervisor',
    body: 'Camada de software que cria e gerencia máquinas virtuais sobre hardware físico. Tipos comuns: VMware vSphere/ESXi, Microsoft Hyper-V, KVM, Xen, Nutanix AHV, Proxmox. Cada VM compartilha o hardware mas roda em isolamento gerenciado pelo hypervisor.',
  },
  'Containers': {
    title: 'Containers',
    body: 'Tecnologia de virtualização leve que empacota aplicação + dependências em uma unidade isolada (geralmente baseada em Docker). Diferente de VMs, compartilha o kernel do host. Kubernetes orquestra containers em produção, lidando com scaling, networking e disponibilidade.',
  },
  'Kubernetes': {
    title: 'Kubernetes (K8s)',
    body: 'Plataforma open-source de orquestração de containers (Google, agora CNCF). Gerencia ciclo de vida de pods, services, autoscaling, secrets, network policies. Em produção exige hardening de cluster (RBAC, network policies, image scanning, runtime security).',
  },
  'OT/ICS': {
    title: 'OT / ICS — Operational Technology / Industrial Control Systems',
    body: 'Sistemas de controle industrial: SCADA, PLCs, DCS, sensores de chão de fábrica. Diferente de TI tradicional: prioriza disponibilidade sobre confidencialidade, equipamentos com 20-30 anos de vida, protocolos proprietários (Modbus, DNP3, OPC). Padrão de referência: IEC 62443.',
  },

  // ─── Identidade & Acesso ───────────────────────────────────────────
  'SAML': {
    title: 'SAML — Security Assertion Markup Language',
    body: 'Protocolo XML para autenticação federada e SSO. Padrão maduro (2.0 de 2005), amplamente suportado por ERPs e SaaS corporativos. Um Identity Provider emite "assertions" assinadas que aplicações confiam — sem replicar credenciais.',
  },
  'OIDC': {
    title: 'OIDC — OpenID Connect',
    body: 'Camada de identidade sobre OAuth 2.0. Mais leve e moderno que SAML, usa JSON Web Tokens (JWT). Comum em apps cloud-native, mobile e APIs REST.',
  },
  'SAML/OIDC': {
    title: 'SAML e OIDC — protocolos de autenticação federada',
    body: 'SAML (XML, dominante em ERPs) e OIDC (JSON/JWT, dominante em cloud/SaaS). Ambos permitem SSO entre múltiplos sistemas usando um IdP central. Aplicações legadas que não suportam nenhum dos dois precisam de conector dedicado.',
  },
  'SSO': {
    title: 'SSO — Single Sign-On',
    body: 'Autenticação única que dá acesso a múltiplas aplicações sem precisar fazer login em cada uma. Implementado tipicamente via SAML, OIDC ou Kerberos. Reduz fadiga de senha e ponto único para revogação no offboarding.',
  },
  'MFA': {
    title: 'MFA — Multi-Factor Authentication',
    body: 'Autenticação com 2+ fatores: algo que você sabe (senha), tem (token, celular) e/ou é (biometria). Mitiga ~99% dos ataques de credencial roubada. Phishing-resistant MFA usa hardware tokens FIDO2/Passkey, imune a phishing reverso.',
  },
  '2FA': {
    title: '2FA — Two-Factor Authentication',
    body: 'Caso específico de MFA com exatamente 2 fatores (geralmente senha + código TOTP/SMS/push). SMS é o fator mais fraco (susceptível a SIM swap). Push notification e TOTP em apps autenticadoras são padrão atual.',
  },
  'CIAM': {
    title: 'CIAM — Customer Identity & Access Management',
    body: 'Diferente do IAM corporativo (empregados), o CIAM gerencia identidades de CLIENTES. Volumes maiores (milhares a milhões), prioriza conversão, federação social (Google/Apple/Facebook), consentimento LGPD/GDPR e self-service.',
  },
  'IAM': {
    title: 'IAM — Identity & Access Management',
    body: 'Disciplina que gerencia identidades digitais (humanos, serviços, APIs) e seus acessos. Inclui autenticação (quem é), autorização (o que pode fazer), provisionamento (criar/revogar) e auditoria. IGA é o subset que governa o ciclo de vida e a conformidade.',
  },
  'IGA': {
    title: 'IGA — Identity Governance & Administration',
    body: 'Camada de governança sobre IAM: campanhas de recertificação, SoD, RBAC formal, provisionamento automatizado (joiner/mover/leaver), trilhas de auditoria. Foco em compliance e redução de privilege creep.',
  },
  'PAM': {
    title: 'PAM — Privileged Access Management',
    body: 'Gestão de acessos privilegiados (root, admin, contas de serviço). Cofre de credenciais com rotação automática, gravação de sessão, MFA reforçado e Just-in-Time access. Exemplos: CyberArk, BeyondTrust, Delinea, Senhasegura.',
  },
  'JIT': {
    title: 'JIT — Just-in-Time Access',
    body: 'Modelo onde usuário não tem privilégio permanente. Toda elevação para admin/root passa por aprovação, dura limitado e gera trilha. Combinado com PAM, reduz exposição de credenciais privilegiadas em >80%.',
  },
  'SoD': {
    title: 'SoD — Segregation of Duties / Segregação de Funções',
    body: 'Princípio que separa responsabilidades incompatíveis (quem lança não aprova, quem aprova não paga). Em IGA, vira regras automáticas que bloqueiam concessões conflitantes — preventivo, antes que o usuário consiga executar.',
  },
  'RBAC': {
    title: 'RBAC — Role-Based Access Control',
    body: 'Modelo que agrupa permissões em papéis (roles) e atribui usuários a papéis. Mais simples de manter que ACLs por usuário. Combinado com ABAC, vira o modelo híbrido moderno.',
  },
  'ABAC': {
    title: 'ABAC — Attribute-Based Access Control',
    body: 'Modelo de autorização baseado em atributos do usuário, recurso, ação e contexto (horário, geolocalização, device posture, sensibilidade do dado). Mais granular que RBAC. Padrão XACML formaliza policies como código.',
  },
  'SCIM': {
    title: 'SCIM — System for Cross-domain Identity Management',
    body: 'Protocolo REST/JSON padronizado para sincronização de identidades entre sistemas. Usado pelo IGA para provisionar/desprovisionar contas em SaaS (M365, Slack, Salesforce, etc.). Substitui scripts customizados por integração padrão.',
  },

  // ─── SOC / Detecção & Resposta ─────────────────────────────────────
  'SOC': {
    title: 'SOC — Security Operations Center',
    body: 'Centro de operações de segurança: equipe + processos + tecnologia para monitorar, detectar e responder a incidentes 24×7 (ou modelo reduzido). Mede-se por MTTD (Mean Time to Detect) e MTTR (Mean Time to Respond). Pode ser próprio ou terceirizado (MSSP/MDR).',
  },
  'SIEM': {
    title: 'SIEM — Security Information and Event Management',
    body: 'Plataforma que agrega logs de múltiplas fontes (firewall, EDR, AD, apps), correlaciona eventos e gera alertas. Casos de uso (use cases) mapeiam comportamentos suspeitos ao MITRE ATT&CK. Exemplos: Splunk, Sentinel, QRadar, Elastic, Wazuh.',
  },
  'SOAR': {
    title: 'SOAR — Security Orchestration, Automation and Response',
    body: 'Camada de automação sobre o SIEM. Executa playbooks pré-definidos quando alertas disparam (ex.: isolar endpoint, bloquear IP, abrir ticket, notificar oncall). Reduz tempo de resposta de minutos para segundos.',
  },
  'EDR': {
    title: 'EDR — Endpoint Detection & Response',
    body: 'Agente em endpoints/servidores que coleta telemetria detalhada (process tree, network, file changes) e detecta comportamento malicioso via heurística + machine learning. Permite contenção remota (isolar host, kill process). Exemplos: CrowdStrike, SentinelOne, MS Defender for Endpoint.',
  },
  'XDR': {
    title: 'XDR — Extended Detection & Response',
    body: 'Evolução do EDR que correlaciona telemetria de múltiplos domínios (endpoint + email + cloud + identity) em uma plataforma única. Reduz alertas isolados e dá visão holística do ataque.',
  },
  'ITDR': {
    title: 'ITDR — Identity Threat Detection & Response',
    body: 'Detecção e resposta especializada em ameaças de identidade: credenciais comprometidas, kerberoasting, golden ticket, ataques a AD/Entra ID. Complementa EDR/XDR — necessário porque 80%+ das brechas envolvem credenciais.',
  },
  'ISPM': {
    title: 'ISPM — Identity Security Posture Management',
    body: 'Visibilidade contínua da postura de identidade: contas dormentes, permissões excessivas, configs inseguras, drift de policies. Análogo a CSPM (cloud) e DSPM (dados), mas focado em identidade.',
  },
  'DLP': {
    title: 'DLP — Data Loss Prevention',
    body: 'Tecnologia que monitora e bloqueia exfiltração de dados sensíveis (CPF, cartão, IP corporativo) via email, web, USB, cloud upload. Funciona com classificação prévia + regras + ML. Modos: endpoint, network, cloud (CASB).',
  },
  'WAF': {
    title: 'WAF — Web Application Firewall',
    body: 'Firewall de camada 7 (aplicação) que inspeciona requisições HTTP/HTTPS. Bloqueia OWASP Top 10 (SQLi, XSS, RCE, etc.), bots, DDoS L7. Modos inline ou reverse-proxy. Exemplos: Cloudflare, F5, AWS WAF, Imperva.',
  },
  'CASB': {
    title: 'CASB — Cloud Access Security Broker',
    body: 'Proxy entre usuários e aplicações cloud (M365, Salesforce, Box, Google Workspace). Aplica policies de DLP, controle de uploads, detecção de shadow IT, autenticação adaptativa. Fundamental para SaaS sprawl.',
  },
  'NAC': {
    title: 'NAC — Network Access Control',
    body: 'Controla quais dispositivos podem se conectar à rede corporativa, baseado em postura (patch level, AV, criptografia). Bloqueia ou coloca em quarentena devices não conformes. Exemplos: Cisco ISE, Forescout, Aruba ClearPass.',
  },
  'ZTNA': {
    title: 'ZTNA — Zero Trust Network Access',
    body: 'Modelo onde nenhuma conexão é confiável por padrão. Acesso a recursos exige autenticação contínua, verificação de postura do device e princípio de menor privilégio. Substitui VPNs tradicionais com acesso por aplicação.',
  },
  'SASE': {
    title: 'SASE — Secure Access Service Edge',
    body: 'Arquitetura que combina SD-WAN + ZTNA + CASB + SWG + FWaaS em serviço cloud-native. Gartner consolidou em 2019. Permite acesso seguro a aplicações de qualquer lugar sem backhaul para datacenter.',
  },
  'HSM': {
    title: 'HSM — Hardware Security Module',
    body: 'Dispositivo dedicado (físico ou cloud) que gera, armazena e usa chaves criptográficas em hardware tamper-resistant. Exige para PKI, assinatura digital, decryption HTTPS em escala, custódia de chaves PIX. Padrões: FIPS 140-2/3.',
  },

  // ─── Frameworks & Regulações ───────────────────────────────────────
  'NIST CSF': {
    title: 'NIST CSF — Cybersecurity Framework',
    body: 'Framework do NIST (EUA) com 6 funções: GOVERN, IDENTIFY, PROTECT, DETECT, RESPOND, RECOVER. Versão 2.0 de 2024 adicionou GOVERN. Não é compliance, é referência de boas práticas. Mais flexível e adotado globalmente que ISO 27001.',
  },
  'ISO 27001': {
    title: 'ISO/IEC 27001:2022',
    body: 'Norma internacional certificável de gestão de segurança da informação (SGSI). Estrutura PDCA + Anexo A com 93 controles (Operacionais, Físicos, Tecnológicos, Pessoais). Certificação custosa mas demandada por contratos B2B.',
  },
  'CIS Controls': {
    title: 'CIS Controls v8.1',
    body: 'Lista priorizada de 18 controles (CIS 1-18) com 153 sub-controles, organizada em 3 Implementation Groups: IG1 (higiene básica, ~30 sub-controles), IG2 (intermediário, ~74), IG3 (avançado, ~153). Mais acionável que ISO 27001 para começar.',
  },
  'MITRE ATT&CK': {
    title: 'MITRE ATT&CK',
    body: 'Knowledge base de táticas, técnicas e procedimentos (TTPs) de adversários reais. Estruturada em 14 táticas (Initial Access → Impact) com ~200 técnicas. Base para casos de uso em SIEM, threat hunting e simulações de adversário.',
  },
  'LGPD': {
    title: 'LGPD — Lei Geral de Proteção de Dados',
    body: 'Lei 13.709/2018 que regula tratamento de dados pessoais no Brasil. Inspirada na GDPR europeia. Fiscalizada pela ANPD. Sanções: até 2% do faturamento ou R$ 50 milhões por infração. Dado sensível (saúde, biometria, etc.) exige base legal mais rígida (art. 11).',
  },
  'PCI-DSS': {
    title: 'PCI-DSS — Payment Card Industry Data Security Standard',
    body: 'Norma obrigatória para qualquer empresa que processa, armazena ou transmite dados de cartão. Versão 4.0 (2022) com 12 requisitos. Não certifica; valida via QSA ou self-assessment SAQ. Nível depende do volume de transações.',
  },
  'BACEN': {
    title: 'BACEN — Resolução BCB 4.658/2018',
    body: 'Resolução do Banco Central que dispõe sobre política de cibersegurança em instituições financeiras. Exige plano de resposta a incidentes, testes anuais, gestão de risco cibernético, contratos com terceiros com requisitos mínimos, comunicação à supervisão em casos relevantes.',
  },
  'SOX': {
    title: 'SOX — Sarbanes-Oxley Act',
    body: 'Lei norte-americana de 2002 sobre governança corporativa e controles internos. Empresas listadas em bolsa nos EUA (ou subsidiárias) precisam comprovar adequação de controles sobre relatórios financeiros (ICFR). Em SegInfo: trilhas de auditoria, SoD, acesso privilegiado.',
  },
  'IEC 62443': {
    title: 'IEC 62443',
    body: 'Família de normas para segurança de Industrial Automation and Control Systems (IACS). Estabelece zonas, conduits, security levels (SL1-4) e requisitos por papel. Referência mandatória para indústria, energia e utilities.',
  },

  // ─── Operação ──────────────────────────────────────────────────────
  'EPS': {
    title: 'EPS — Events Per Second',
    body: 'Métrica de volume de eventos que o SIEM precisa processar por segundo. Dimensionamento de SIEM: ambientes pequenos < 500 EPS, médios 500-5k, grandes 5k-50k, enterprise +50k. Cada EPS adiciona custo de licença e infraestrutura.',
  },
  'Awareness': {
    title: 'Security Awareness Program',
    body: 'Programa estruturado de educação de usuários: treinamentos periódicos, simulações de phishing, comunicações orientadas, métricas de click rate. Mitiga o risco humano (engenharia social, manipulação) que tecnologia sozinha não resolve.',
  },
  'Phishing': {
    title: 'Phishing',
    body: 'Engenharia social via e-mail, SMS (smishing) ou voz (vishing) para induzir vítima a clicar link malicioso, fornecer credenciais ou aprovar transação. Spear phishing é direcionado; BEC (Business Email Compromise) impersona executivos. Principal vetor inicial em 70%+ das brechas.',
  },
  'Playbook': {
    title: 'Playbook de Resposta a Incidentes',
    body: 'Documento que padroniza passos específicos para um cenário (ransomware, account takeover, data leak). Define gatilhos, decisões, responsáveis, comunicações. Combina com runbook técnico (passos operacionais detalhados) e plano IR (governança).',
  },
  'DAST': {
    title: 'DAST — Dynamic Application Security Testing',
    body: 'Testes de segurança em aplicação rodando (vs. SAST que olha código estático). Simula ataque externo: SQLi, XSS, autenticação quebrada, OWASP Top 10. Pode ser contínuo via integração CI/CD ou agendado. Complementa pen-test manual.',
  },
  'SAST': {
    title: 'SAST — Static Application Security Testing',
    body: 'Análise de segurança no código-fonte sem executá-lo. Identifica padrões inseguros (SQLi, hardcoded secrets, weak crypto) em build time. Falsos positivos altos, mas oferece feedback imediato ao desenvolvedor. Exemplos: SonarQube, Veracode, Checkmarx, Semgrep.',
  },
  'VPN': {
    title: 'VPN — Virtual Private Network',
    body: 'Túnel criptografado entre cliente e gateway corporativo. Modelo tradicional dá acesso de rede full (camada 3) — risco de movimentação lateral. ZTNA substitui por acesso granular por aplicação. Protocolos: IPsec, OpenVPN, WireGuard.',
  },
  'IPS': {
    title: 'IPS — Intrusion Prevention System',
    body: 'Inspeciona tráfego em tempo real e BLOQUEIA padrões de ataque (signatures, anomaly, protocol). Diferente do IDS que apenas alerta. Tipicamente embutido em NGFW. Pode ser host-based (HIPS) ou network-based (NIPS).',
  },
  'IDS': {
    title: 'IDS — Intrusion Detection System',
    body: 'Sistema de detecção de intrusão que monitora tráfego e gera alertas sem bloquear. Útil em modo "tap" para visibilidade sem impacto. Hoje, muitas funções migraram para NDR (Network Detection & Response).',
  },
  'NDR': {
    title: 'NDR — Network Detection & Response',
    body: 'Sucessor do IDS tradicional. Coleta telemetria de rede (flow, packet, metadata), aplica ML para detectar anomalias e padrões de adversário. Foco em movimentação lateral, C2 covert channels, exfiltration. Complementa EDR e SIEM.',
  },
  'Tabletop': {
    title: 'Tabletop Exercise',
    body: 'Simulação de incidente em sala (sem executar tecnicamente). Time discute decisões em cenário hipotético (ransomware, breach LGPD). Testa playbooks, comunicação, autoridade de decisão. Diferente de Red Team (técnico) e Purple Team (colaborativo).',
  },
  'Backup': {
    title: 'Backup',
    body: 'Cópia de dados/sistemas para restauração em caso de perda. Regra 3-2-1: 3 cópias, 2 mídias, 1 offsite (idealmente air-gapped). Backups imutáveis (WORM) protegem contra ransomware. Teste de restore real (não só backup) é o que mais falha.',
  },
  'Sandbox': {
    title: 'Sandbox',
    body: 'Ambiente isolado para detonar arquivos/URLs suspeitos sem risco. Comum em email security gateways: anexo é executado em VM isolada antes de chegar ao usuário. Permite identificar 0-days que assinaturas tradicionais ignoram.',
  },
  'Honeypot': {
    title: 'Honeypot',
    body: 'Sistema "isca" intencionalmente vulnerável para atrair atacantes. Detecta reconnaissance interno, credenciais comprometidas (canary tokens), movimentação lateral. Honeynet é uma rede inteira de honeypots; deception platforms automatizam.',
  },
  'CDN': {
    title: 'CDN — Content Delivery Network',
    body: 'Rede de servidores edge que cacheia conteúdo próximo ao usuário, reduzindo latência. CDNs modernas (Cloudflare, Akamai, Fastly) integram WAF, DDoS protection, bot management. Camada essencial para apps expostas à internet.',
  },
  'DDoS': {
    title: 'DDoS — Distributed Denial of Service',
    body: 'Ataque que sobrecarrega serviço com tráfego de múltiplas fontes. Volumétrico (G/Tbps), Protocol (SYN flood), Application (HTTP flood). Mitigação exige CDN/scrubbing center upstream do datacenter. Crescimento de IoT amplia botnets disponíveis.',
  },
  'PKI': {
    title: 'PKI — Public Key Infrastructure',
    body: 'Infraestrutura de chaves públicas: CAs, sub-CAs, certificados, CRLs/OCSP. Base de TLS/HTTPS, S/MIME, code signing, mTLS, smart cards. ICP-Brasil é PKI nacional regulada pelo ITI. Gestão de ciclo de vida (emissão, renovação, revogação) é dor recorrente.',
  },
}

const GLOSSARY_TERMS = Object.keys(GLOSSARY).sort((a, b) => b.length - a.length)

// ─── Context para gerenciar modal aberto globalmente ──────────────────

type GlossaryCtx = { open: (term: string) => void }
const GlossaryContext = createContext<GlossaryCtx | null>(null)

export function GlossaryProvider({ children }: { children: ReactNode }) {
  const [term, setTerm] = useState<string | null>(null)
  const entry = term ? GLOSSARY[term] : null

  return (
    <GlossaryContext.Provider value={{ open: setTerm }}>
      {children}
      {entry && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setTerm(null)}
        >
          <div
            className="bg-slate-900 border border-cyan-700/60 rounded-xl shadow-2xl w-full max-w-lg p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-bold mb-1">
                  Glossário técnico
                </div>
                <h3 className="text-lg font-bold text-white leading-tight">{entry.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setTerm(null)}
                className="text-slate-400 hover:text-slate-200 flex-shrink-0"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">{entry.body}</p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setTerm(null)}
                className="px-4 py-2 rounded-md bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-bold"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}
    </GlossaryContext.Provider>
  )
}

export function useGlossary() {
  const ctx = useContext(GlossaryContext)
  return ctx || { open: () => {} }
}

// ─── Helpers de renderização ──────────────────────────────────────────

/**
 * Renderiza um texto com botões (?) ao lado de cada termo do glossário.
 * O termo é detectado por regex; preserva o texto original.
 */
export function renderWithGlossary(label: string) {
  if (!label) return label
  const escaped = GLOSSARY_TERMS.map(t => t.replace(/[/\\.+*?^$()|[\]{}]/g, '\\$&')).join('|')
  // \b funciona mal com termos contendo / (ex: SAML/OIDC). Permitimos boundary lateral por whitespace/pontuação.
  const regex = new RegExp(`(${escaped})`, 'g')
  const parts = label.split(regex)
  return (
    <>
      {parts.map((part, i) => {
        if (GLOSSARY[part]) {
          return (
            <span key={i} className="inline-flex items-baseline gap-1">
              <span>{part}</span>
              <GlossaryHintButton term={part} />
            </span>
          )
        }
        return <span key={i}>{part}</span>
      })}
    </>
  )
}

export function GlossaryHintButton({ term }: { term: string }) {
  const { open } = useGlossary()
  const info = GLOSSARY[term]
  if (!info) return null
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); open(term) }}
      className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-cyan-500/20 text-cyan-300 text-[10px] font-bold border border-cyan-500/40 hover:bg-cyan-500/40 transition cursor-help align-middle"
      aria-label={`Definição de ${term}`}
      title={info.title}
    >
      ?
    </button>
  )
}
